import { type LocationQueryRaw, type RouteLocationRaw } from "vue-router";
import urlSlug from "url-slug";
import { slugifyItemName } from "@/router/linkFactoriesHelpers";
import { createLocalizedRoute } from "@/router/helpers";
import { buildFilterPath } from "@/composables/filter/filterPathCodec";
import { FilterSlug, ParameterSlug_Category, serializeCollectibleIdsParam } from "@/services/filter/filterHelpers";
import type { FilterValues } from "@/services/filter/filterTypes";
import type { Sort } from "@/services/marketplace/item/marketplaceItemTypes";
import { ItemStatusToUrlString } from "@/services/collection/collectionUrl";
import { ItemStatus } from "@/services/collection/item/collectionItemTypes";
import type { UrlFlags as ChatUrlFlags } from "@/composables/chat/urlFlags";
import { RouteName } from "@/router/routeNameEnum";
import { SPORT_SUBCATEGORY_FILTER_SLUG, THEME_FILTER_SLUG } from "@/services/filter/filterSchema";
import type { Category } from "@/services/collection/categories/categoriesTypes";
import type * as CollectorsFilter from "@/services/collectors/collectorsFilter";
import type { CollectionItemId } from "@/services/collection/item/collectionItemTypes";
import type { CollectionItem } from "@/services/collection/item/collectionItemTypes";
import type { PublicCollectionItem_FromCollectible } from "@/services/collection/item/public/publicCollectionItemTypes";
import type { PublicCollectionItem_ListItem, PublicCollectionItem } from "@/services/collection/item/public/publicCollectionItemTypes";
import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import type { UserId } from "@/services/user/userTypes";
import type { MarketplaceItemId } from "@/services/marketplace/item/marketplaceItemTypes";
import type { OrderId } from "@/services/marketplace/order/orderTypes";
import type { CollectionItemElastic } from "@/services/collection/item/elastic/collectionItemElasticSchema";
import type { SupportAnchor } from "@/services/support/supportTypes";
import { isMarketplaceListingActive, type MarketplaceListingCandidate } from "@/services/marketplace/item/marketplaceItemHelpers";

/**
 * ==============================================
 * Messages
 * ==============================================
 */

export const createMessagesLink = (buddyId?: UserId, buddyNickname?: string, flags?: ChatUrlFlags): RouteLocationRaw => {
  const route: RouteLocationRaw = {
    name: RouteName.Messages,
  };

  if (buddyId !== undefined && buddyNickname !== undefined) {
    route.params = { buddyId, buddyNickname };
  }

  if (flags !== undefined) {
    route.query = flags;
  }

  return createLocalizedRoute(route);
};

/**
 * ==============================================
 * Collectibles (database/catalogue)
 * ==============================================
 */

export const createCatalogueLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Catalogue });

/** Catalogue item link from a category's url slug directly — for callers that have the slug but not the full Category. */
export const createCollectibleLinkByCategoryUrl = (categoryUrl: string, itemId: CollectibleId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueItem,
    params: { categoryUrl, itemId },
  });

export const createCollectibleLink = (category: Category, itemId: CollectibleId): RouteLocationRaw => createCollectibleLinkByCategoryUrl(category.url, itemId);

export const createCatalogueCategoryLink = (category: Category): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueCategory,
    params: {
      categoryUrl: category.url,
    },
  });

// Catalogue category browse pre-filtered to a single set/theme via the `theme` filter path segment
// (/database/<category>/theme:<slug>), where `themeSlug` is the theme's filter slug (Theme.slug).
// Takes the category url slug directly so callers with only the slug (not the full Category) can use it too.
export const createCatalogueThemeFilterLink = (categoryUrl: string, themeSlug: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueCategory,
    params: { categoryUrl, filterPath: buildFilterPath({ [THEME_FILTER_SLUG]: themeSlug }, []) },
  });

// Catalogue category browse pre-filtered to a single sport via the `podkategorie` filter path
// segment (/database/<category>/podkategorie:<slug>), e.g. the Sports Cards category filtered to
// Hokej or Fotbal. Takes the category url slug directly for the same reason as the theme link above.
export const createCatalogueSubcategoryFilterLink = (categoryUrl: string, subcategorySlug: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueCategory,
    params: { categoryUrl, filterPath: buildFilterPath({ [SPORT_SUBCATEGORY_FILTER_SLUG]: subcategorySlug }, []) },
  });

// Catalogue category browse pre-filtered by an arbitrary set of filter-path values (facet slug →
// value(s), plus the `identification` text filter). Slugs are written to the path unordered — no
// filter definitions are needed at the call site.
export const createCatalogueCategoryFilterLink = (categoryUrl: string, filterValues: FilterValues): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueCategory,
    params: { categoryUrl, filterPath: buildFilterPath(filterValues, []) },
  });

/**
 * ==============================================
 * Auth
 * ==============================================
 */

export const createRegistrationLink = (email?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Registration,
    query: { email },
  });

export const createLoginLink = (redirectAfterLogin?: string, email?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Login,
    query: {
      redirect_url: redirectAfterLogin,
      email,
    },
  });

export const createPasswordRestoreLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.PasswordRestore });

/**
 * ==============================================
 * Magazine
 * ==============================================
 */

export const createArticleLink = (id: number, url: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Article,
    params: { id, url },
  });

export const createNewsArticleLink = (slug: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.NewsArticle,
    params: { slug },
  });

export const createMagazineLink = (category?: Category): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Magazine,
    params: {
      category: category ? category.url : undefined,
    },
  });

/**
 * ==============================================
 * Collection
 * ==============================================
 */

export const createMyItemDetailLinkById = (id: CollectionItemId, name: string, hash?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MyItemDetail,
    params: {
      id,
      nameSeoSlug: slugifyItemName(name),
    },
    hash,
  });

export const createMyItemDetailLink = (item: PublicCollectionItem_ListItem | CollectionItem | PublicCollectionItem | PublicCollectionItem_FromCollectible | CollectionItemElastic, hash?: string): RouteLocationRaw => createMyItemDetailLinkById(item.id, item.name, hash);

export const createCollectionItemNewLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.CollectionItemNew });

/**
 * ==============================================
 * Curated lists
 * ==============================================
 */

// `query` lets callers deep-link the browse with a pre-filled fulltext search, e.g. { q: "Black Bolt" }.
export const createCuratedListsLink = (query?: LocationQueryRaw): RouteLocationRaw => createLocalizedRoute({ name: RouteName.CuratedLists, query });

export const createCuratedListLink = (id: number, slug?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CuratedListDetail,
    params: { id, slug: slug || undefined },
  });

export const createCuratedListPublicProgressLink = (id: number, nickname: string, slug?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CuratedListPublicProgress,
    params: { id, nickname, slug: slug || undefined },
  });

export const createCuratedListUserProgressLink = (nickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CuratedListUserProgress,
    params: { nickname },
  });

/**
 * ==============================================
 * AI identification
 * ==============================================
 */

export const createAiLookupFormLink = (categoryUrl?: string): RouteLocationRaw => {
  if (categoryUrl !== undefined) {
    return createLocalizedRoute({ name: RouteName.AiFormWithPreselectedCategory, params: { seoSlug: categoryUrl } });
  } else {
    return createLocalizedRoute({ name: RouteName.AiForm });
  }
};

/**
 * ==============================================
 * Account
 * ==============================================
 */

export const createAccountSettingsLink = (anchor?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.AccountSettings,
    hash: anchor ? `#${anchor}` : undefined,
  });

export const createYearInReviewLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.YearInReview });

export const createLeaderboardLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Leaderboard });

/**
 * ==============================================
 * Marketplace
 * ==============================================
 */

export const createShoppingCartLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.ShoppingCart });

export const createMarketplaceOrderLink = (sellerNickname: string): RouteLocationRaw => createLocalizedRoute({ name: RouteName.MarketplaceOrder, params: { sellerNickname } });

export const createSalesLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Sales });

export const createSaleLink = (orderId: OrderId): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Sale, params: { orderId } });

export const createPurchasesLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Purchases });

export const createPurchaseLink = (orderId: OrderId): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Purchase, params: { orderId } });

export const createNegotiationsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Negotiations });

export type MarketplaceLinkParams = {
  category?: string;
  query?: string;
  sort?: Sort;
  page?: number;
  perPage?: number;
  /** Preset collectible-ids prefilter (deduped by the serializer). */
  collectibleIds?: CollectibleId[];
  /** Preset owner prefilter (deep link to a single collector's marketplace offers). */
  ownerId?: UserId;
};

export const createMarketplaceLink = (params: MarketplaceLinkParams = {}): RouteLocationRaw => {
  const { category, query, sort, page, perPage, collectibleIds, ownerId } = params;
  return createLocalizedRoute({
    name: RouteName.Marketplace,
    params: { filterPath: buildFilterPath(category ? { [ParameterSlug_Category]: category } : {}, []) },
    query: { query, sort, page, perPage, [FilterSlug.CollectibleIds]: serializeCollectibleIdsParam(collectibleIds ?? []), [FilterSlug.OwnerId]: ownerId !== undefined ? String(ownerId) : undefined },
  });
};

// Marketplace browse pre-filtered to a single facet value via the filter path segment
// (/marketplace/<category>/<filterSlug>:<valueSlug>), e.g. category + product type or condition.
// Single facet depth only — combine only category with one filter slug at a time.
export const createMarketplaceFilterLink = (categoryUrl: string, filterSlug: string, valueSlug: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Marketplace,
    params: { filterPath: buildFilterPath({ [ParameterSlug_Category]: categoryUrl, [filterSlug]: valueSlug }, []) },
  });

// Marketplace browse pre-filtered to a category plus several facet values at once, e.g. a sport
// (podkategorie) combined with product type — used by the per-sport/per-edice description text,
// which links back into the already-selected sport rather than resetting it.
export const createMarketplaceMultiFilterLink = (categoryUrl: string, filters: FilterValues): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Marketplace,
    params: { filterPath: buildFilterPath({ [ParameterSlug_Category]: categoryUrl, ...filters }, []) },
  });

export const createMarketplaceItemLink = (marketplaceItemId: MarketplaceItemId, itemName: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItem,
    params: { marketplaceItemId, seoSlug: slugifyItemName(itemName) },
  });

export const createMarketplaceItemSavedLink = (collectionItemId: CollectionItemId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItemSaved,
    params: { collectionItemId },
  });

export const createMarketplaceItemNewLink = (collectionItemId: CollectionItemId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItemNew,
    params: { collectionItemId },
  });

export const createMarketplaceItemEditLink = (collectionItemId: CollectionItemId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItemEdit,
    params: { collectionItemId },
  });

/**
 * ==============================================
 * Legals
 * ==============================================
 */

export const createGdprLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Gdpr });

export const createTacLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Tac });

export const createGuideLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Guide });

/**
 * ==============================================
 * Others / not sorted yet
 * ==============================================
 */

export const createMyProfileLink = (): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collector,
  });

export const createCollectionLink = (): RouteLocationRaw => createCollectionStatusLink(ItemStatus.InMyCollection);

export const createCollectionStatusLink = (itemStatus?: ItemStatus): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collection,
    params: { itemStatus: itemStatus ? ItemStatusToUrlString[itemStatus] : undefined },
  });

export const createError404Link = (): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Error404,
    params: {
      pathMatch: "404",
    },
  });

export const createCollectorLink = (nickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collector,
    params: { nickname, filterPath: [] },
  });

export const createHomepageLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Homepage });

export const createPublicItemDetailLink = (itemId: number, itemName: string, collectorNickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.PublicItemDetail,
    params: {
      nickname: urlSlug(collectorNickname),
      itemId: itemId,
      slug: urlSlug(itemName) || "-",
    },
  });

/**
 * A collection item's detail link — the marketplace item when it's actually for sale
 * (see `isMarketplaceListingActive`), otherwise the public collection item detail.
 */
export const createItemDetailLink = (itemId: number, itemName: string, ownerNickname: string, marketplaceItem: (MarketplaceListingCandidate & { id: MarketplaceItemId }) | null | undefined): RouteLocationRaw => (isMarketplaceListingActive(marketplaceItem) ? createMarketplaceItemLink(marketplaceItem.id, itemName) : createPublicItemDetailLink(itemId, itemName, ownerNickname));

export const createAboutLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.About });

export const createContactsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Contacts });

export const createWhatColectioOffersLink = (anchor?: WhatColectioOffersAnchor): RouteLocationRaw => createLocalizedRoute({ name: RouteName.WhatColectioOffers, hash: anchor ? `#${anchor}` : undefined });

export enum WhatColectioOffersAnchor {
  SecureMarketplace = "secure-marketplace",
  AiFeatures = "ai-features",
  UniqueCollection = "unique-collection",
}

export const createCollectorsLink = (filter?: CollectorsFilter.Filter): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collectors,
    query: filter?.createUrlParams(),
  });

export const createSavedItemsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.SavedItems });

export const createSupportLink = (anchor?: SupportAnchor): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Support, hash: anchor ? `#${anchor}` : undefined });
